import React, { useMemo, useState } from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useMarkVehicleHiringEntryAttendanceMutation } from "store/services/vehicleHiringEntryApi";
import { useAddNoteSourceMutation } from "store/services/noteApi";

const toDateKey = (date) => new Date(new Date(date).toDateString()).getTime();
const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const buildMonthGrid = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startOffset = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    return cells;
};

const VehicleHiringEntryAttendanceCalendar = ({ entryDetail }) => {
    const hiringDates = entryDetail?.hiringDates || [];

    const [currentMonth, setCurrentMonth] = useState(() =>
        startOfMonth(hiringDates[0] ? new Date(hiringDates[0]) : new Date())
    );
    const [pendingDay, setPendingDay] = useState(null); // { date, targetStatus }
    const [noteOpen, setNoteOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [noteError, setNoteError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const [markAttendance] = useMarkVehicleHiringEntryAttendanceMutation();
    const [addNoteSource] = useAddNoteSourceMutation();

    const hiringDateKeys = useMemo(
        () => new Set(hiringDates.map((d) => toDateKey(d))),
        [hiringDates]
    );
    const absentKeys = useMemo(
        () => new Set((entryDetail?.absentDates || []).map((d) => toDateKey(d))),
        [entryDetail?.absentDates]
    );

    const cells = useMemo(() => buildMonthGrid(currentMonth), [currentMonth]);

    const canEditAttendance = ["Daily", "Monthly"].includes(entryDetail?.hiringType);

    if (!hiringDates.length) return null;

    const monthKeys = hiringDates.map((d) => {
        const dt = new Date(d);
        return `${dt.getFullYear()}-${dt.getMonth()}`;
    });
    const availableMonths = [...new Set(monthKeys)].sort();
    const currentMonthKey = `${currentMonth.getFullYear()}-${currentMonth.getMonth()}`;
    const currentMonthIdx = availableMonths.indexOf(currentMonthKey);
    const canGoPrev = currentMonthIdx > 0;
    const canGoNext = currentMonthIdx >= 0 && currentMonthIdx < availableMonths.length - 1;

    const goToMonthOffset = (offset) => {
        const idx = currentMonthIdx + offset;
        if (idx < 0 || idx >= availableMonths.length) return;
        const [year, month] = availableMonths[idx].split("-").map(Number);
        setCurrentMonth(new Date(year, month, 1));
    };

    const handleDayClick = (date) => {
        if (!canEditAttendance) return;
        const key = toDateKey(date);
        if (!hiringDateKeys.has(key)) return;
        const isAbsent = absentKeys.has(key);
        setPendingDay({ date, targetStatus: isAbsent ? "Working" : "Absent" });
    };

    const handleConfirmChange = () => {
        setNoteText("");
        setNoteError("");
        setNoteOpen(true);
    };

    const handleCancelConfirm = () => setPendingDay(null);
    const handleCancelNote = () => {
        setNoteOpen(false);
        setPendingDay(null);
    };

    const handleSubmitNote = async () => {
        if (noteText.trim().length < 3) {
            setNoteError("Note must be at least 3 characters.");
            return;
        }
        setIsSaving(true);
        try {
            await addNoteSource({
                content: `Leave Reason: ${noteText.trim()}`,
                type: "Note",
                source: "VehicleHiringEntry",
                sourceRef: entryDetail._id,
            }).unwrap();
            await markAttendance({
                id: entryDetail._id,
                date: pendingDay.date.toISOString(),
                action: pendingDay.targetStatus === "Absent" ? "mark" : "unmark",
            }).unwrap();
            toast.success(`Marked as ${pendingDay.targetStatus}`);
            setNoteOpen(false);
            setPendingDay(null);
        } catch (err) {
            toast.error(err?.data?.message ?? "Failed to update attendance");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box mt={3} mb={3}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Attendance
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <IconButton size="small" disabled={!canGoPrev} onClick={() => goToMonthOffset(-1)}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography fontWeight={600}>
                        {currentMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
                    </Typography>
                    <IconButton size="small" disabled={!canGoNext} onClick={() => goToMonthOffset(1)}>
                        <ChevronRight />
                    </IconButton>
                </Box>
                <Grid container columns={7} spacing={0.5}>
                    {WEEKDAY_LABELS.map((label) => (
                        <Grid item xs={1} key={label}>
                            <Typography variant="caption" color="text.secondary" align="center" display="block">
                                {label}
                            </Typography>
                        </Grid>
                    ))}
                    {cells.map((date, idx) => {
                        if (!date) return <Grid item xs={1} key={`blank-${idx}`} />;
                        const key = toDateKey(date);
                        const isHiringDate = hiringDateKeys.has(key);
                        const isAbsent = absentKeys.has(key);
                        return (
                            <Grid item xs={1} key={key}>
                                <Box
                                    onClick={() => handleDayClick(date)}
                                    sx={{
                                        aspectRatio: "1 / 1",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 1,
                                        fontSize: "0.8rem",
                                        cursor: isHiringDate && canEditAttendance ? "pointer" : "default",
                                        bgcolor: !isHiringDate
                                            ? "transparent"
                                            : isAbsent
                                                ? "error.light"
                                                : "success.light",
                                        color: isHiringDate ? "common.white" : "text.disabled",
                                        "&:hover": isHiringDate && canEditAttendance ? { opacity: 0.8 } : {},
                                    }}
                                >
                                    {date.getDate()}
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
                <Box display="flex" gap={2} mt={2}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 12, height: 12, bgcolor: "success.light", borderRadius: 0.5 }} />
                        <Typography variant="caption">Working</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Box sx={{ width: 12, height: 12, bgcolor: "error.light", borderRadius: 0.5 }} />
                        <Typography variant="caption">Absent</Typography>
                    </Box>
                </Box>
                {!canEditAttendance && (
                    <Typography variant="caption" color="text.secondary" mt={1} display="block">
                        Attendance is view-only for this hiring type.
                    </Typography>
                )}
            </Paper>

            <Dialog open={!!pendingDay && !noteOpen} onClose={handleCancelConfirm}>
                <DialogTitle>Change attendance?</DialogTitle>
                <DialogContent>
                    <Typography>
                        {pendingDay &&
                            `Want to change ${pendingDay.date.toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                            })} to ${pendingDay.targetStatus}?`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelConfirm}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmChange}>
                        Yes, Continue
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={noteOpen} onClose={handleCancelNote} fullWidth maxWidth="sm">
                <DialogTitle>Add a note for this change</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        margin="dense"
                        label="Note"
                        value={noteText}
                        onChange={(e) => {
                            setNoteText(e.target.value);
                            if (noteError) setNoteError("");
                        }}
                        error={!!noteError}
                        helperText={noteError || "Minimum 3 characters."}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelNote} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmitNote} disabled={isSaving}>
                        {isSaving ? <CircularProgress size={20} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default VehicleHiringEntryAttendanceCalendar;